<script setup lang="ts">
/**
 * Appoint / Withdraw MD (B32115) — SG 일반 프리셋 Studio 패널.
 *   문서 1종: Directors' Resolution (Appointment & Resignation of MD) — 이사 전원 서명([[Signers]]).
 *   회사 선택 → 회사명·UEN·이사(서명자)·현직 MD(사임자, 1명일 때) 자동채움
 *   → 새 MD(✨ 연락처) · 사임 MD(✨ 회사 MD 목록) · 발효일 · 결의일 입력 → Create Task.
 *   결의 종류 3택(선임 / 해임 / 선임+해임) — 챗봇 또는 문서 상단 스위치. 템플릿은 include_md_appointment / include_md_resignation 로 행을 남기거나 지운다.
 *   E-Filing = AppointWithdrawOfficerEFiling(BM03B, positionHeld LC_MD) — presetForm.values + persons 로 시드.
 */
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { buildContactDataFromSelectedPerson } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/appointDirectorContact';
import { applyDirectorStructuredProfileFromContactData } from '@/features/startTask/preset_task/sg/components/appoint-director/composables/useFormPayload';
import { createEmptyAppointDirectorForm } from '@/features/startTask/preset_task/sg/components';
import { nationalityLabelForOutput } from '@/features/startTask/preset_task/sg/composables/cmBaseCodeOptions';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, typeOf } from './final-dividends/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from './final-dividends/cdd/agmSignersContext';
import AppointWithdrawMdPackageDoc from './appoint-withdraw-md/AppointWithdrawMdPackageDoc.vue';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
  selectedPerson?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-person-lookup', context: Record<string, any>): void;
}>();

const TASK_CODE = 'B32115';
/** L_OFFICER 직책 코드 — Managing Director / Director. */
const POS_MD = 'B30029';
const POS_DIRECTOR = 'B30027';

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const taskConfigSteps = ref<string[]>([]);
const lastAutofillCd = ref('');
const autofilledKeys = ref<Set<string>>(new Set());

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const effectiveTitle = computed(() => String(props.taskTitle || '').trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

const t = (v: unknown) => String(v ?? '').trim();

/** 결의 종류(챗봇 3택 · 문서 상단 스위치) — appoint / withdraw / both. 문서는 고른 결의만 남는다. */
const ACTIONS = ['appoint', 'withdraw', 'both'];
const action = ref('both');
const hasAppointment = computed(() => action.value !== 'withdraw');
const hasResignation = computed(() => action.value !== 'appoint');
watch(() => props.chatFormData?.mdAction, (v) => { if (ACTIONS.includes(String(v))) action.value = String(v); }, { immediate: true });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

/**
 * E-Filing(BM03B) 용 인물 정보 — 문서에는 안 나가고 presetForm.persons 로 저장.
 *   AppointWithdrawOfficerEFiling 이 이걸로 선임/해임 행(ID·국적·주소·연락처)을 시드한다(이름이 같을 때만).
 */
type PersonInfo = Record<string, string>;
const persons = ref<{ appoint: PersonInfo; resign: PersonInfo }>({ appoint: {}, resign: {} });
function personInfo(p: Record<string, any>, name: string): PersonInfo {
  const tmp = createEmptyAppointDirectorForm();
  try { applyDirectorStructuredProfileFromContactData(tmp, buildContactDataFromSelectedPerson(p as OfficerDetail)); } catch { /* 원본 필드로 대체 */ }
  const natCd = t(p.NATIONALITY_CD || p.nationalityCd);
  return {
    name,
    contactCd: t(p.contactCd || p.CONTACT_CD),
    idNo: t(p.nricNo || p.passportNo || p.contactRegNo || p.CONTACT_REG_NO || p.identificationNo),
    idType: t(p.idTypeName || p.idType || tmp.directorIdTypeCd),
    nationality: t(p.nationalityName || p.nationality) || (natCd ? nationalityLabelForOutput(natCd) : ''),
    dateOfBirth: t(p.dateOfBirth || p.DATE_OF_BIRTH || p.dob).slice(0, 10),
    email: t(p.contactEmail || p.email || p.CONTACT_EMAIL),
    address: t(tmp.directorAddress || p.residentialAddress || p.address || p.primaryAddress),
    alternateAddress: t(tmp.directorAlternateAddress || p.alternateAddress || p.contactAddress),
    mobileNo: t(p.mobileNo || p.MOBILE_NO || p.phone),
    countryCd: t(tmp.directorMobileCountryCd || p.countryCd),
    areaCode: t(tmp.directorMobileAreaCode || p.areaCode),
  };
}

// ✨ 인물 조회 — 새 MD = 글로벌 연락처, 사임 MD = 이 회사의 현직 MD.
/** 조회를 요청한 칸 — 결과는 이 칸에 넣는다(조회 중 다른 칸 클릭에 영향받지 않게). */
let lookupKey = '';
fill.setActivateHandler((key: string) => {
  if (typeOf(key) === 'entity') { emit('change-entity'); return; }
  if (key === 'md_name') {
    lookupKey = key;
    emit('request-person-lookup', { fieldGroup: 'md-appoint', scopedKey: key, defaultRole: 'CONTACT', lookupType: 'contact', subjectLabel: 'New MD (contact)' });
  } else if (key === 'resigning_md_name') {
    lookupKey = key;
    emit('request-person-lookup', { fieldGroup: 'md-resign', scopedKey: key, defaultRole: 'MANAGING_DIRECTOR', lookupType: 'officer', entityCd: entityCd.value, subjectLabel: 'Resigning MD' });
  }
});
watch(() => props.selectedPerson, (p) => {
  if (!p) return;
  const key = lookupKey || fill.activeKey.value;
  lookupKey = '';
  if (key !== 'md_name' && key !== 'resigning_md_name') return;
  const nm = t(p.name || p.contactName || p.fullName || `${p.firstName || ''} ${p.lastName || ''}`);
  if (nm) {
    fill.setValue(key, nm);
    persons.value[key === 'md_name' ? 'appoint' : 'resign'] = personInfo(p as Record<string, any>, nm);
    emitProgress();
  }
});

let signerSeq = 0;
function mkSigner(name: string): SignerEntry {
  return { id: `id-sg-md-${signerSeq++}`, name: t(name), title: 'Director', role: 'director', isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

// ── 자동채움: 회사 선택 → 회사명·UEN·이사(서명자)·현직 MD ──
async function autofillFromEntity(cdRaw: string) {
  const cd = t(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  if (entityChanged) { signersCtx.clearAll(); persons.value = { appoint: {}, resign: {} }; }
  const written = new Set<string>();
  const set = (k: string, v: unknown) => { const s = t(v); if (s) { fill.setValue(k, s); written.add(k); } };

  set('company_name', props.chatFormData?.entityName || props.entityData?.companyName);
  set('company_registration_no', props.chatFormData?.registrationNo || props.entityData?.registrationNo);
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
  } catch (e) { console.warn('[AppointWithdrawMd] agm-source failed', e); }

  const officers = await loadOfficers(cd);
  seedDirectorSigners(officers);
  // 현직 MD 가 1명이면 사임자로 미리 채움(여러 명이면 ✨ 로 선택).
  const mds = namesWithPosition(officers, POS_MD, /managing director/i);
  if (mds.length === 1 && !t(fill.values.resigning_md_name)) {
    set('resigning_md_name', mds[0]);
    const o = officers.find((x: any) => t(x?.contactName || x?.name || x?.fullName) === mds[0]);
    if (o) persons.value.resign = personInfo(o, mds[0]);
  }
  autofilledKeys.value = written;
  emitProgress();
}

async function loadOfficers(cd: string): Promise<any[]> {
  if (!cd) return [];
  try {
    const res: any = await docGenApi.getEntityOfficersAll(cd);
    return res?.data?.officers ?? res?.data?.data?.officers ?? [];
  } catch (e) { console.warn('[AppointWithdrawMd] officers-by-entity-all failed', e); return []; }
}

function namesWithPosition(officers: any[], positionCd: string, nameRe: RegExp): string[] {
  const names: string[] = [];
  for (const o of officers) {
    const positions: any[] = Array.isArray(o?.positions) ? o.positions : [];
    const hit = positions.some((p) => String(p?.positionCd || '') === positionCd || nameRe.test(String(p?.positionName || '')));
    const nm = t(o?.contactName || o?.name || o?.fullName);
    if (hit && nm && !names.includes(nm)) names.push(nm);
  }
  return names;
}

/** 이사 전원 = DR 서명자. 이미 서명자가 있으면 건드리지 않는다. */
function seedDirectorSigners(officers: any[]) {
  if (signersCtx.signersOf('dr_Signers').length) return;
  // MD 는 직책명에 'Director' 가 들어가므로 이사 판별은 코드 또는 정확히 'Director' 인 경우만(Managing/Alternate 제외).
  const names = namesWithPosition(officers, POS_DIRECTOR, /^\s*director\s*$/i);
  if (names.length) signersCtx.setSigners('dr_Signers', names.map(mkSigner));
}

watch(() => props.chatFormData, (fd) => {
  const cd = t(fd?.entityCd || fd?.icpCd);
  if (cd && !editMode.value) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd && !editMode.value) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(t(props.selection.task.TASK_CODE));
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

// ── Edit 지원 ──
function gatherState() {
  return { values: { ...fill.exportPayload() }, signers: signersCtx.exportAll(), entityCd: entityCd.value, action: action.value, persons: persons.value };
}
async function restoreState(s: any) {
  if (!s) return;
  if (ACTIONS.includes(String(s.action))) action.value = String(s.action);
  if (s.persons && typeof s.persons === 'object') persons.value = { appoint: s.persons.appoint || {}, resign: s.persons.resign || {} };
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null) fill.setValue(k, String(v));
  }
  if (s.signers) signersCtx.importAll(s.signers);
  if (!signersCtx.signersOf('dr_Signers').length) seedDirectorSigners(await loadOfficers(t(s.entityCd) || entityCd.value));
  emitProgress();
}

// ── Create / Update ──
const REQUIRED: Array<[string, string, ('appoint' | 'resign')?]> = [
  ['company_name', 'company'],
  ['md_name', 'new MD', 'appoint'],
  ['md_appointment_date', 'appointment date', 'appoint'],
  ['resigning_md_name', 'resigning MD', 'resign'],
  ['md_resignation_date', 'resignation date', 'resign'],
  ['resolution_date', 'resolution date'],
];

async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  if (!entityCd.value) { progress.value = 'Please select a company first.'; return; }
  const v = fill.exportPayload();
  const missing = REQUIRED
    .filter(([, , part]) => !part || (part === 'appoint' ? hasAppointment.value : hasResignation.value))
    .filter(([k]) => !t(v[k])).map(([, label]) => label);
  if (missing.length) { progress.value = `Please fill in: ${missing.join(', ')}.`; return; }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = t(props.selection.task.TASK_CODE) || TASK_CODE;
    const docFormData: Record<string, any> = {
      company_name: t(v.company_name),
      company_registration_no: t(v.company_registration_no),
      include_md_appointment: hasAppointment.value,
      include_md_resignation: hasResignation.value,
      md_name: hasAppointment.value ? t(v.md_name) : '',
      md_appointment_date: hasAppointment.value ? t(v.md_appointment_date) : '',
      resigning_md_name: hasResignation.value ? t(v.resigning_md_name) : '',
      md_resignation_date: hasResignation.value ? t(v.md_resignation_date) : '',
      resolution_date: t(v.resolution_date),
    };
    const taskFormData = {
      taskTypeCd,
      entityCd: entityCd.value,
      companyName: docFormData.company_name,
      companyRegistrationNo: docFormData.company_registration_no,
      mdAction: action.value,
      mdName: docFormData.md_name,
      mdAppointmentDate: docFormData.md_appointment_date,
      resigningMdName: docFormData.resigning_md_name,
      mdResignationDate: docFormData.md_resignation_date,
      resolutionDate: docFormData.resolution_date,
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: taskFormData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value,
          taskFlow: {
            createDocument: steps.length === 0 ? true : steps.includes('doc'),
            requestTSign: steps.includes('t-sign'),
            proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling'),
          },
          officerData: taskFormData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0) || null;
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; return; }
    }

    progress.value = 'Saving documents…';
    const boardSigners = signersCtx.signersOf('dr_Signers');
    const seen = new Set<number>();
    const docsToSave = (props.selection.documents || [])
      .filter((d: any) => {
        const tid = Number(d?.tmpId || 0);
        if (!tid || seen.has(tid)) return false;
        seen.add(tid); return true;
      })
      .map((d: any) => ({
        templateId: Number(d.tmpId),
        label: String(d.templateName || d.docCode || 'Document').replace(/\.docx?$/i, '').trim(),
        personName: docFormData.company_name,
        formData: { ...docFormData },
        signers: boardSigners.length ? boardSigners : undefined,
        entityCd: entityCd.value,
      }));
    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) { progress.value = `⚠ Document save failed: ${err?.message || err}`; }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="md-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Appoint / Withdraw MD' }}</div>
        <div class="co-subtitle">Directors' Resolution — Appointment &amp; Resignation of Managing Director (Singapore)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div class="co-doc">
      <AppointWithdrawMdPackageDoc v-model:action="action" />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || !isReady || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.md-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.md-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
